// What to suggest for each dropped file: its name, then — when the listening
// pass had time for it — what it sounds like, and a tempo for a loop whose
// name and length said nothing. Pure over what `listenTo` already decoded;
// never throws, never blocks the upload.

import type { NewTag } from "@shared/api-types.js";
import { drumGuess } from "@web-shared/lib/tags/audio-features";
import { mergeSuggestions } from "@web-shared/lib/tags/suggest";
import { folderSegments, suggestFromFolder, suggestFromName } from "@web-shared/lib/tags/suggest-name";
import { CONFIDENT_TEMPO } from "@web-shared/lib/warp";

import { guessClip } from "./clip-meta";
import type { Heard } from "./listen";
import type { ReadFile } from "./read-files";

/** Suggestions by file path. A file with nothing to say has no entry. */
export function suggestForClips(files: readonly ReadFile[], heard: Heard | null): Map<string, NewTag[]> {
  const out = new Map<string, NewTag[]>();
  // The folder every file shares is the pack's own name, not a tag for each clip.
  const roots = new Set(files.map((f) => f.file.path.split("/")[0] ?? ""));
  const dropRoot = files.length > 1 && roots.size === 1 && files.every((f) => f.file.path.includes("/"));
  files.forEach((f, i) => {
    let tags: NewTag[] = [];
    try {
      const fromName = suggestFromName(f.file.name);
      // The path speaks too, nearest folder first: Drums/Hats/closed_hat.wav
      // says Hi-hat even when the file's name does not.
      const fromFolders = folderSegments(f.file.path, { dropRoot }).flatMap((seg) => suggestFromFolder(seg));
      const said = [...fromName, ...fromFolders];
      const fromAudio: NewTag[] = [];
      const features = heard?.features[i] ?? null;
      if (features) {
        const drum = drumGuess(features);
        // The name's (or the folder's) say on what sound it is stands; audio fills a silence.
        if (drum && !said.some((t) => t.tag.startsWith("drums:") || t.tag.startsWith("sounds:"))) fromAudio.push({ tag: drum, source: "audio" });
      }
      // A key, for what is tonal: never on a drum (by name, folder or sound),
      // and never over a key the name already gives.
      const key = heard?.keys[i] ?? null;
      const drum = said.some((t) => t.tag.startsWith("drums:")) || fromAudio.some((t) => t.tag.startsWith("drums:"));
      if (key && !drum && !said.some((t) => t.tag.startsWith("key:"))) fromAudio.push({ tag: key.tag, source: "audio" });
      const guess = guessClip(f.file.name, f.probe.durationSec);
      const tempo = heard?.tempos[i] ?? null;
      if (guess.bpm === null && f.probe.durationSec >= 1.5 && tempo && tempo.confidence >= CONFIDENT_TEMPO) {
        fromAudio.push({ tag: `tempo:${Math.round(tempo.bpm)}`, source: "audio" });
      }
      tags = mergeSuggestions(fromName, fromFolders, fromAudio);
    } catch {
      tags = [];
    }
    if (tags.length > 0) out.set(f.file.path, tags);
  });
  return out;
}
